import { Injectable, WritableSignal, Signal, signal, computed } from '@angular/core';

/**
 * A service to manage the navigation menu's collapsed state, providing methods to collapse, uncollapse, and toggle the state.
 * The state is synced with local storage to ensure persistence across user sessions.
 */
@Injectable({
  providedIn: 'root'
})
export class CollapseService {
  /**
   * Internal signal holding the current collapse state (true if collapsed).
   */
  private readonly collapseStateSignal: WritableSignal<boolean> = signal(false);

  /**
   * Read-only signal providing access to the current collapse state.
   */
  public readonly collapsed: Signal<boolean> = computed(() => this.collapseStateSignal());

  /**
   * Initializes the collapse service and sets the initial collapse state by reading from local storage.
   */
  constructor() {
    this.collapseStateSignal.set(
      (localStorage.getItem('navCollapsed') === 'true')
    );
  }

  /**
   * Collapses the navigation menu, setting the collapse state to `true` and storing it in local storage.
   */
  public collapse(): void {
    this.collapseStateSignal.set(true);
    localStorage.setItem('navCollapsed', 'true');
  }

  /**
   * Uncollapses the navigation menu, setting the collapse state to `false` and storing it in local storage.
   */
  public uncollapse(): void {
    this.collapseStateSignal.set(false);
    localStorage.setItem('navCollapsed', 'false');
  }

  /**
   * Toggles the current collapse state of the navigation menu.
   * If collapsed, it becomes uncollapsed, and vice versa.
   * The updated state is stored in local storage.
   */
  public toggle(): void {
    const state = this.collapsed();
    this.collapseStateSignal.set(!state);
    localStorage.setItem('navCollapsed', state ? 'false' : 'true');
  }
}
